import React, { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Box, Typography, TextField, Button, Paper, InputAdornment,
  IconButton, Alert, CircularProgress, LinearProgress,
} from '@mui/material';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import API_BASE_URL from '../../Config/config';

const TSGLogo = ({ size = 44 }) => {
  const [imgError, setImgError] = useState(false);
  if (imgError) {
    return (
      <Box sx={{
        width: size, height: size, borderRadius: '12px',
        background: 'linear-gradient(135deg,#6C3BFF,#8B5CF6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto',
      }}>
        <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: size * 0.46 }}>T</Typography>
      </Box>
    );
  }
  return (
    <Box
      component="img"
      src="https://skillgenie-college-uploads.s3.ap-south-1.amazonaws.com/TSG_Logo.png"
      alt="The Skill Genie"
      loading="lazy"
      onError={() => setImgError(true)}
      sx={{ height: size, width: 'auto', objectFit: 'contain', display: 'block', mx: 'auto' }}
    />
  );
};

const getPasswordStrength = (pass) => {
  if (!pass) return { score: 0, label: '', color: '#E2E8F0' };
  let score = 0;
  if (pass.length >= 8) score++;
  if (/[A-Z]/.test(pass)) score++;
  if (/[a-z]/.test(pass)) score++;
  if (/\d/.test(pass)) score++;
  if (/[@$!%*?&]/.test(pass)) score++;
  const levels = [
    { label: '', color: '#E2E8F0' },
    { label: 'Very weak', color: '#EF4444' },
    { label: 'Weak', color: '#F97316' },
    { label: 'Fair', color: '#EAB308' },
    { label: 'Good', color: '#22C55E' },
    { label: 'Strong', color: '#16A34A' },
  ];
  return levels[score];
};

const NextGenResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const strength = getPasswordStrength(password);
  const strengthScore = ['','Very weak','Weak','Fair','Good','Strong'].indexOf(strength.label);

  const validatePassword = (pass) =>
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(pass);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validatePassword(password))
      return setError('Password must be 8+ chars with uppercase, lowercase, number & special character');
    if (password !== confirmPassword)
      return setError('Passwords do not match');
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/nextgen-builder/reset-password/${token}`,
        { password }
      );
      if (res.data.success) {
        setSuccess(true);
        setTimeout(() => navigate('/nextgen-builder/login'), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh', bgcolor: '#F8FAFC',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      px: { xs: 2, sm: 3 }, py: 5,
      fontFamily: '"Plus Jakarta Sans","Inter",system-ui,sans-serif',
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Background blobs */}
      <Box sx={{ position:'absolute',top:-160,right:-160,width:480,height:480,borderRadius:'50%',
                  background:'radial-gradient(circle,rgba(108,59,255,0.06) 0%,transparent 70%)',
                  pointerEvents:'none' }} />
      <Box sx={{ position:'absolute',bottom:-120,left:-120,width:380,height:380,borderRadius:'50%',
                  background:'radial-gradient(circle,rgba(139,92,246,0.05) 0%,transparent 70%)',
                  pointerEvents:'none' }} />

      <motion.div
        initial={{ opacity:0, y:18 }}
        animate={{ opacity:1, y:0 }}
        transition={{ duration:0.35, ease:[0.25,0.1,0.25,1] }}
        style={{ width:'100%', maxWidth:420, position:'relative', zIndex:1 }}
      >
        {/* Back link */}
        {!success && (
          <Box sx={{ mb:3 }}>
            <Link to="/nextgen-builder/login" style={{ textDecoration:'none', display:'inline-flex',
                alignItems:'center', gap:6, color:'#64748B', fontWeight:600, fontSize:'0.85rem' }}>
              <ArrowBackRoundedIcon sx={{ fontSize:16 }} />
              Back to Sign In
            </Link>
          </Box>
        )}

        <Paper elevation={0} sx={{
          p:{ xs:3.5, sm:5 },
          borderRadius:'20px',
          border:'1px solid #E5E7EB',
          bgcolor:'#FFFFFF',
          boxShadow:'0 1px 3px rgba(0,0,0,0.06),0 4px 8px rgba(0,0,0,0.04),0 20px 48px -12px rgba(108,59,255,0.1)',
        }}>
          {success ? (
            /* ── Success state ────────────────────────────── */
            <Box sx={{ textAlign:'center', py:1 }}>
              <Box sx={{
                width:64, height:64, borderRadius:'18px', mx:'auto', mb:3,
                bgcolor:'rgba(22,163,74,0.08)', border:'1px solid rgba(22,163,74,0.18)',
                display:'flex', alignItems:'center', justifyContent:'center',
              }}>
                <CheckCircleRoundedIcon sx={{ color:'#16A34A', fontSize:32 }} />
              </Box>
              <Typography variant="h5" sx={{ color:'#0F172A', fontWeight:800, mb:1 }}>
                Password reset!
              </Typography>
              <Typography variant="body2" sx={{ color:'#64748B', lineHeight:1.75, mb:3.5 }}>
                Your password has been updated successfully. Redirecting you to sign in…
              </Typography>
              <Button
                component={Link} to="/nextgen-builder/login"
                fullWidth size="large"
                sx={{
                  py:1.65, borderRadius:'12px', fontWeight:700, fontSize:'0.95rem', color:'#fff',
                  background:'linear-gradient(135deg, #6C3BFF 0%, #8B5CF6 100%)',
                  boxShadow:'0 4px 14px rgba(108,59,255,0.32)',
                  '&:hover':{ background:'linear-gradient(135deg, #5020E0 0%, #7C3AED 100%)',
                    boxShadow:'0 8px 28px rgba(108,59,255,0.44)', transform:'translateY(-1px)' },
                }}
              >
                Sign In Now
              </Button>
            </Box>
          ) : (
            /* ── Reset form ───────────────────────────────── */
            <>
              <Box sx={{ textAlign:'center', mb:3.5 }}>
                <Box sx={{
                  width:56, height:56, borderRadius:'16px', mx:'auto', mb:2.5,
                  bgcolor:'rgba(108,59,255,0.08)', border:'1px solid rgba(108,59,255,0.18)',
                  display:'flex', alignItems:'center', justifyContent:'center',
                }}>
                  <LockResetRoundedIcon sx={{ color:'#6C3BFF', fontSize:28 }} />
                </Box>
                <Typography variant="h5" sx={{ color:'#0F172A', fontWeight:800, mb:0.75 }}>
                  Set new password
                </Typography>
                <Typography variant="body2" sx={{ color:'#64748B', maxWidth:320, mx:'auto' }}>
                  Choose a strong password for your TSG Nexus account.
                </Typography>
              </Box>

              {error && <Alert severity="error" sx={{ mb:2.5 }}>{error}</Alert>}

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <TextField
                  fullWidth label="New Password" name="password" required
                  type={showPass ? 'text' : 'password'}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPass((p) => !p)} edge="end" size="small"
                          sx={{ color:'#94A3B8', '&:hover':{ color:'#6C3BFF', bgcolor:'rgba(108,59,255,0.07)' } }}>
                          {showPass ? <VisibilityOffRoundedIcon fontSize="small" /> : <VisibilityRoundedIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb:1 }}
                />

                {/* Password strength bar */}
                {password && (
                  <Box sx={{ mb:2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={(strengthScore / 5) * 100}
                      sx={{
                        height:4, borderRadius:99, bgcolor:'#E2E8F0', mb:0.75,
                        '& .MuiLinearProgress-bar': {
                          borderRadius:99,
                          bgcolor: strength.color,
                          transition:'background-color 0.3s ease',
                        },
                      }}
                    />
                    {strength.label && (
                      <Typography variant="caption" sx={{ color: strength.color, fontWeight:600 }}>
                        {strength.label}
                      </Typography>
                    )}
                  </Box>
                )}

                <TextField
                  fullWidth label="Confirm New Password" name="confirmPassword" required
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  error={!!confirmPassword && password !== confirmPassword}
                  helperText={confirmPassword && password !== confirmPassword ? 'Passwords do not match' : ''}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirm((p) => !p)} edge="end" size="small"
                          sx={{ color:'#94A3B8', '&:hover':{ color:'#6C3BFF', bgcolor:'rgba(108,59,255,0.07)' } }}>
                          {showConfirm ? <VisibilityOffRoundedIcon fontSize="small" /> : <VisibilityRoundedIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb:3 }}
                />

                <Button
                  type="submit" fullWidth size="large" disabled={loading || (!!confirmPassword && password !== confirmPassword)}
                  sx={{
                    py:1.65, borderRadius:'12px', fontWeight:700, fontSize:'0.95rem',
                    letterSpacing:'-0.01em', color:'#fff',
                    background:'linear-gradient(135deg, #6C3BFF 0%, #8B5CF6 100%)',
                    boxShadow:'0 4px 14px rgba(108,59,255,0.32)',
                    '&:hover': {
                      background:'linear-gradient(135deg, #5020E0 0%, #7C3AED 100%)',
                      boxShadow:'0 8px 28px rgba(108,59,255,0.44)',
                      transform:'translateY(-1px)',
                    },
                    '&:disabled':{ opacity:0.6, boxShadow:'none', transform:'none' },
                  }}
                >
                  {loading ? <CircularProgress size={20} sx={{ color:'#fff' }} /> : 'Reset Password'}
                </Button>
              </Box>

              <Box sx={{ textAlign:'center', mt:3 }}>
                <Typography variant="body2" sx={{ color:'#64748B' }}>
                  Remember your password?{' '}
                  <Link to="/nextgen-builder/login"
                    style={{ color:'#6C3BFF', fontWeight:700, textDecoration:'none' }}>
                    Sign In
                  </Link>
                </Typography>
              </Box>
            </>
          )}
        </Paper>
      </motion.div>
    </Box>
  );
};

export default NextGenResetPassword;
